import { DocumentRoot } from "../../api/models/document-root";
import { TreeViewDataItem } from "./DocumentPartTree";
import { fetchActionWorkflow } from "../../api/document/DocumentsPart";
import { WorkflowStatus } from "../../api/models/workflow-status";
import React, { PropsWithChildren } from "react";
import { IDocumentPartProps } from "./IDocumentPartProps";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { ContextualMenuItemType, IContextualMenuItem } from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { isAxiosError } from "axios";
import NotificationManager from "../../common/notification/NotificationManager";
import { hasApproveAction, isApproveAction, REJECT_ACTION } from "./workflowActions";
import { resolveWorkflowActionUser } from "./workflowActionUser";

const refreshArrayDocumentPartWithStatus = (newElement: DocumentPartWithStatus[], array: DocumentPartWithStatus[]) => {
  array = array.map((element) => {
    const matchingElement = newElement?.find((newEl) => newEl.documentPartId === element.documentPartId);
    return matchingElement || element;
  });
  return array;
};

function resolveDisplayedUserName(workflowStatus?: WorkflowStatus | null): string | undefined {
  return workflowStatus?.displayUserName ?? workflowStatus?.userUpd;
}

export function propagateDisplayUserNameRename(
  array: DocumentPartWithStatus[],
  previousDisplayedUserName: string | undefined,
  newUserName: string | undefined
): DocumentPartWithStatus[] {
  if (!previousDisplayedUserName || !newUserName || previousDisplayedUserName === newUserName) {
    return array;
  }
  return array.map((entry) => {
    if (resolveDisplayedUserName(entry.workflowStatus) !== previousDisplayedUserName) {
      return entry;
    }
    return { ...entry, workflowStatus: { ...entry.workflowStatus, displayUserName: newUserName } };
  });
}

/**
 * Utils functions
 */
export function mapFormToDataItem(part: DocumentRoot): TreeViewDataItem {
  return {
    text: part.documentPart.name.replace(/\.docx$/, ""),
    key: part.documentPart.id,
    hasChildren: part.documentPart.hasChildren,
    nodeType: "part",
    expanded: false,
    items: undefined,
    data: part,
    hasReadPermissions: part.hasReadPermissions,
  };
}

export function changeState(
  item: TreeViewDataItem,
  action: string,
  setDisableActionWorkflow: React.Dispatch<React.SetStateAction<boolean>>,
  props: PropsWithChildren<IDocumentPartProps>,
  stateOfRootId: string,
  comment: string
) {
  let workflowStatus: WorkflowStatus;
  const workflowActionUser = resolveWorkflowActionUser();
  setDisableActionWorkflow(true);
  props.setDocumentPartWithStatus((prevState) => {
    prevState.forEach((documentPartWithStatus) => {
      if (documentPartWithStatus.documentPartId === item.key) {
        workflowStatus = documentPartWithStatus.workflowStatus;
        documentPartWithStatus.workflowStatus = null;
      }
    });
    return prevState;
  });
  const previousDisplayedUserName = resolveDisplayedUserName(workflowStatus);
  fetchActionWorkflow(stateOfRootId, item.key, action, comment, workflowActionUser)
    .then((items) => {
      props.setDocumentPartWithStatus((prevState) => {
        const refreshed = refreshArrayDocumentPartWithStatus(items, prevState);
        return propagateDisplayUserNameRename(refreshed, previousDisplayedUserName, workflowActionUser);
      });
      setDisableActionWorkflow(false);
      localStorage.removeItem(`comment-${item.key}`);
    })
    .catch((err) => {
      if (isAxiosError(err) && err.response) {
        const status = err.response.status;

        if (status === 503) {
          NotificationManager.addNotification({
            title: "",
            type: "waiting",
            message: formatMessage("cof.base.system.align"),
          });
          restoreWorkflowStatus(item.key, workflowStatus, props, setDisableActionWorkflow);
          return;
        }
      }
      restoreWorkflowStatus(item.key, workflowStatus, props, setDisableActionWorkflow);
    });
}

export function restoreWorkflowStatus(
  id: string,
  workflowStatus: WorkflowStatus,
  props: PropsWithChildren<IDocumentPartProps>,
  setDisableActionWorkflow: React.Dispatch<React.SetStateAction<boolean>>
) {
  props.setDocumentPartWithStatus((prevState) => {
    prevState.forEach((documentPartWithStatus) => {
      if (documentPartWithStatus.documentPartId === id) {
        documentPartWithStatus.workflowStatus = workflowStatus;
      }
    });
    return prevState;
  });
  setDisableActionWorkflow(false);
}

export function getWorkflowStatus(
  itemId: string,
  documentPartWithStatuses: DocumentPartWithStatus[]
): WorkflowStatus | undefined {
  return documentPartWithStatuses?.find((item) => item.documentPartId === itemId)?.workflowStatus;
}

export function checkApproveStatus(workflowStatus: WorkflowStatus) {
  return (
    workflowStatus?.workflowStatusCode === "30" ||
    workflowStatus?.workflowStatusCode === "31" ||
    workflowStatus?.workflowStatusCode === "32" ||
    workflowStatus?.workflowStatusCode === "33" ||
    workflowStatus?.workflowStatusCode === "34"
  );
}

export function checkNextApprover(actions: string[]) {
  return hasApproveAction(actions);
}

export function getStyle(action: string, enableCheckout: boolean) {
  // Historically case-insensitive, unlike the other action checks: normalized here to keep that.
  const normalizedAction = action?.trim().toUpperCase();
  if (isApproveAction(normalizedAction)) {
    return { fontWeight: "bold", color: enableCheckout ? "#b7b7b7" : "#93c33a" };
  }
  if (normalizedAction === REJECT_ACTION) {
    return { fontWeight: "bold", color: enableCheckout ? "#b7b7b7" : "red" };
  }
  return { fontWeight: "bold", color: enableCheckout ? "#b7b7b7" : "black" };
}

export function getLastModifiedDate(lastUpdate: string) {
  return lastUpdate + " UTC";
}

export function addUserUpdateHeaderItem(infoItems: any[], userUpdInfo) {
  infoItems.push({
    key: "userUpdateHeader",
    itemType: ContextualMenuItemType.Header,
    topDivider: false,
    bottomDivider: false,
    text: userUpdInfo,
    disabled: true,
    styles: {
      root: {
        borderTop: "none",
        paddingTop: 0,
        cursor: "default",
        color: "#000",
      },
    },
  });
}

export function addLastUpdateHeaderItem(infoItems: any[], lastUpdate: string) {
  infoItems.push({
    key: "lastUpdateHeader",
    topDivider: false,
    bottomDivider: false,
    itemType: ContextualMenuItemType.Normal,
    text: lastUpdate,
    disabled: true,
    styles: {
      root: {
        borderTop: "none",
        paddingTop: 0,
        cursor: "default",
        color: "#000000",
      },
    },
  });
}

export function addInfoSection(options: IContextualMenuItem[], infoItems: any[]) {
  options.push({
    key: "infoSection",
    itemType: ContextualMenuItemType.Section,
    sectionProps: {
      items: infoItems,
      topDivider: false,
      bottomDivider: false,
    },
    styles: {
      root: {
        borderTop: "none",
        paddingTop: 0,
        cursor: "default",
        color: "#000",
        textAlign: "center",
      },
    },
    disabled: true,
  });
}

export function addMenuSection(options: IContextualMenuItem[], items: IContextualMenuItem[]) {
  options.push({
    key: "menuSection",
    itemType: ContextualMenuItemType.Section,
    sectionProps: {
      bottomDivider: true,
      items: items,
    },
  });
}

export function addInfoTooltip(
  myMap: Map<string, number>,
  splitName: string,
  workflowStatusCode: string,
  options: IContextualMenuItem[]
) {
  let correctMessage = getCorrectMessage(myMap, splitName, workflowStatusCode);
  if (correctMessage !== "") {
    options.push({
      key: "infoTooltip",
      itemType: ContextualMenuItemType.Header,
      text: correctMessage,
      topDivider: false,
      bottomDivider: false,
      styles: {
        root: {
          borderTop: "none",
          paddingTop: 0,
          cursor: "default",
          color: "normal",
        },
      },
      disabled: true,
    });
  }
}

function getCorrectMessage(map: Map<string, number>, splitName: string, workflowStatusCode: string) {
  if (map.size === 1) {
    return formatMessage("cof.base.submitted.to") + " " + splitName;
  } else if (map.size > 1) {
    return checkMultiApprover(map, workflowStatusCode);
  } else return "";
}

function checkMultiApprover(userMap: Map<string, number>, workflowStatusCode: string) {
  const newWorkflowStatusCode = parseInt(workflowStatusCode, 10);
  const escluded = new Set<number>();

  for (let i = 1; i <= newWorkflowStatusCode - 30; i++) {
    escluded.add(i);
  }

  const userForLevel = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
  };

  userMap?.forEach((level, user) => {
    if (!escluded.has(level)) {
      userForLevel[level].push(user);
    }
  });

  for (let level = 1; level <= 5; level++) {
    if (escluded.has(level)) continue;
    const users = userForLevel[level];
    if (users.length === 1) return formatMessage("cof.base.submitted.to") + " " + users[0];
    if (users.length > 1) return formatMessage("cof.base.submitted.to.multiple.user");
  }
}

/**
 * Additive helper: expand the tree along the ancestor chain returned by
 * `fetchRecursiveParentsDocumentPart` so that `targetNodeId` becomes visible.
 * Returns the updated `treeItems` array.
 *
 * This is used by DocumentPartTree to restore tree selection after closing search.
 */
export async function expandToNodeAndSelect(
  treeItems: TreeViewDataItem[],
  ancestorChain: import("../../api/models/document-position").DocumentPosition[],
  setElementToUpdate: (
    value:
      | ((
          prevState: import("../../api/models/document-root").DocumentRoot[]
        ) => import("../../api/models/document-root").DocumentRoot[])
      | import("../../api/models/document-root").DocumentRoot[]
  ) => void
): Promise<TreeViewDataItem[]> {
  if (!ancestorChain?.length) return treeItems;

  // IDS-5756 round 4: read-only navigator — does NOT mutate currentItems.
  const findItem = (items: TreeViewDataItem[] | undefined, idx: number[]): TreeViewDataItem | undefined => {
    if (!items?.length) return undefined;
    let current: TreeViewDataItem | undefined = items[idx[0]];
    for (let i = 1; i < idx.length && current; i++) current = current.items?.[idx[i]];
    return current;
  };

  let currentItems = treeItems;
  let hierarchicalIndex = "0";

  for (const position of ancestorChain) {
    const pagesToFetch = position.page ?? 0;
    for (let p = 0; p <= pagesToFetch; p++) {
      const { fetchExpandedNodeExternal } = await import("./DocumentPartTreeHelper");
      const existingItem = findItem(currentItems, hierarchicalIndex.split("_").map(Number));
      // Skip the call when the ancestor is already loaded — fetchExpandedNodeExternal toggles `expanded` and would collapse it. See IDS-5756 round 4.
      if (p === 0 && existingItem?.expanded === true && existingItem?.items !== undefined) {
        continue;
      }
      currentItems = await fetchExpandedNodeExternal(
        hierarchicalIndex,
        false,
        p,
        true,
        currentItems,
        setElementToUpdate
      );
    }
    hierarchicalIndex += `_${position.order ?? 0}`;
  }

  // Expand the final level (where the target node lives)
  const { fetchExpandedNodeExternal } = await import("./DocumentPartTreeHelper");
  const finalExistingItem = findItem(currentItems, hierarchicalIndex.split("_").map(Number));
  // Skip the call when the ancestor is already loaded — fetchExpandedNodeExternal toggles `expanded` and would collapse it. See IDS-5756 round 4.
  if (!(finalExistingItem?.expanded === true && finalExistingItem?.items !== undefined)) {
    currentItems = await fetchExpandedNodeExternal(hierarchicalIndex, false, 0, true, currentItems, setElementToUpdate);
  }

  return currentItems;
}
